import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import {
  TopicConditionMode,
  type TopicCondition as TopicConditionType,
} from "@redotech/redo-flows-common/rules/schemas";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import {
  RedoSingleSelectDropdownInput,
  RedoSingleSelectDropdownInputButton,
} from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { useSupportV2Enabled } from "../../../../v2/providers/use-support-v2-enabled";
import { useTopics } from "../../../../v2/setting/topics/use-topics";

const modeOptions = (): RedoListItem<TopicConditionMode>[] => [
  {
    id: TopicConditionMode.DEFAULT,
    type: "text",
    text: t("Default topics"),
    value: TopicConditionMode.DEFAULT,
  },
  {
    id: TopicConditionMode.CUSTOM,
    type: "text",
    text: t("Custom topics"),
    value: TopicConditionMode.CUSTOM,
  },
];

export const TopicCondition = memo(function TopicCondition({
  condition,
  onChange,
  editableFields,
}: {
  condition: TopicConditionType;
  onChange: (updated: TopicConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const supportV2 = useSupportV2Enabled();
  const isEditable = (field: string) =>
    editableFields === undefined || editableFields.has(field);

  // Fall back to the default-topics picker while the flag is still loading.
  if (supportV2.pending) {
    return (
      <DefaultTopicPicker
        condition={condition}
        editable={isEditable("topics")}
        onChange={onChange}
      />
    );
  }

  const mode = condition.mode ?? TopicConditionMode.DEFAULT;
  // Mirror the assignment-condition.tsx pattern: the mode discriminator and
  // the active inner field are gated separately so a template can lock the
  // mode while still letting the merchant pick selections, or vice versa.
  const modeEditable = isEditable("mode");
  const innerFieldEditable =
    modeEditable ||
    isEditable(
      mode === TopicConditionMode.CUSTOM ? "customTopicIds" : "topics",
    );

  const selectedModeOption = modeOptions().find((opt) => opt.value === mode);

  const handleModeChange = (newMode: TopicConditionMode) => {
    if (newMode === mode) {
      return;
    }
    // Clear the unused side's selections when switching modes so the
    // serialized condition only carries data the evaluator will use.
    switch (newMode) {
      case TopicConditionMode.DEFAULT:
        onChange({ ...condition, mode: newMode, customTopicIds: [] });
        break;
      case TopicConditionMode.CUSTOM:
        onChange({ ...condition, mode: newMode, topics: [] });
        break;
      default:
        assertNever(newMode);
    }
  };

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput<TopicConditionMode>
        disabled={!modeEditable}
        fitToAnchor
        label={t("Topic source")}
        options={modeOptions()}
        optionSelected={({ item }) => handleModeChange(item.value)}
        selectedItem={selectedModeOption}
      />
      {mode === TopicConditionMode.DEFAULT ? (
        <DefaultTopicPicker
          condition={condition}
          editable={innerFieldEditable}
          onChange={onChange}
        />
      ) : (
        <CustomTopicPicker
          condition={condition}
          editable={innerFieldEditable}
          onChange={onChange}
        />
      )}
    </Flex>
  );
});

const DefaultTopicPicker = memo(function DefaultTopicPicker({
  condition,
  onChange,
  editable,
}: {
  condition: TopicConditionType;
  onChange: (updated: TopicConditionType) => void;
  editable: boolean;
}) {
  const { t } = useTranslation();
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const topicsLoad = useLoad(
    () => supportRpcClient.getConversationTopics({}),
    [supportRpcClient],
  );

  const topics = useMemo(
    () => topicsLoad.value?.topics ?? [],
    [topicsLoad.value],
  );

  const topicItems: RedoListItem<string>[] = useMemo(
    () =>
      topics.map((topic) => ({
        id: topic,
        type: "text" as const,
        text: topic,
        value: topic,
      })),
    [topics],
  );

  const selectedItems: RedoListItem<string>[] = useMemo(
    () =>
      (condition.topics ?? []).map((topic) => ({
        id: topic,
        type: "text" as const,
        text: topic,
        value: topic,
      })),
    [condition.topics],
  );

  const displayItem: RedoListItem<string> | undefined = useMemo(() => {
    if (selectedItems.length === 0) {
      return undefined;
    }
    if (selectedItems.length === 1) {
      return selectedItems[0];
    }
    return {
      id: "display",
      type: "text" as const,
      text: selectedItems
        .flatMap((item) => ("text" in item ? [item.text] : []))
        .join(", "),
      value: "",
    };
  }, [selectedItems]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="secondary">
        {t("Topic")}
      </Text>
      <RedoSingleSelectDropdownInputButton
        disabled={!editable}
        isDropdownOpen={dropdownOpen}
        onClick={() => editable && setDropdownOpen(!dropdownOpen)}
        placeholder={
          topicItems.length === 0
            ? t("No default topics available")
            : t("Select topics")
        }
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={topicItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          if (!editable) {
            return;
          }
          onChange({ ...condition, topics: items.map((item) => item.value) });
        }}
        size="xs"
      />
      {topicItems.length === 0 && topicsLoad.value !== undefined && (
        <Text fontSize="xs" textColor="secondary">
          {t(
            "Default topics are seeded globally. Ask your {{brandName}} team to populate the catalog, or switch this condition to Custom topics.",
            { brandName: "Redo" },
          )}
        </Text>
      )}
    </Flex>
  );
});

const CustomTopicPicker = memo(function CustomTopicPicker({
  condition,
  onChange,
  editable,
}: {
  condition: TopicConditionType;
  onChange: (updated: TopicConditionType) => void;
  editable: boolean;
}) {
  const { t } = useTranslation();
  const { topics, isLoading } = useTopics();
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // The list is sorted by topic name server-side so the UI stays stable as
  // the catalog grows.
  const topicItems: RedoListItem<string>[] = useMemo(() => {
    return topics.map((topic) => ({
      id: topic.id,
      type: "text" as const,
      text: topic.name,
      value: topic.id,
    }));
  }, [topics]);

  const selectedIds = useMemo(
    () => new Set(condition.customTopicIds ?? []),
    [condition.customTopicIds],
  );

  const selectedItems: RedoListItem<string>[] = useMemo(
    () => topicItems.filter((item) => selectedIds.has(item.value)),
    [topicItems, selectedIds],
  );

  const displayItem: RedoListItem<string> | undefined = useMemo(() => {
    if (selectedItems.length === 0) {
      return undefined;
    }
    if (selectedItems.length === 1) {
      return selectedItems[0];
    }
    return {
      id: "display",
      type: "text" as const,
      text: selectedItems
        .flatMap((item) => ("text" in item ? [item.text] : []))
        .join(", "),
      value: "",
    };
  }, [selectedItems]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="secondary">
        {t("Custom topic")}
      </Text>
      <RedoSingleSelectDropdownInputButton
        disabled={!editable}
        isDropdownOpen={dropdownOpen}
        onClick={() => editable && setDropdownOpen(!dropdownOpen)}
        placeholder={
          topicItems.length === 0
            ? t("No custom topics defined yet")
            : t("Select topics")
        }
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={topicItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          if (!editable) {
            return;
          }
          onChange({
            ...condition,
            customTopicIds: items.map((item) => item.value),
          });
        }}
        size="xs"
      />
      {topicItems.length === 0 && !isLoading && (
        <Text fontSize="xs" textColor="secondary">
          {t("Define topics under Settings → Support → Topics.")}
        </Text>
      )}
    </Flex>
  );
});
